
import { defineComponent, markRaw, PropType, shallowRef } from 'vue';
import GridRow from '@/components/3.6/GridRow.vue';
import GridRowData from '@/models/grid/GridRowData';
import { GridColumnType } from '@/constants/GridColumnType';
import { formFieldsStoreV36 } from '@/store';

import DataPreferenceSettings from '@/components/3.6/modals/DataPreferenceSettings.vue';
import ImageViewer from '@/components/3.6/modals/ImageViewer.vue';
import { FieldIds } from '@/constants/FieldIds';

export default defineComponent({
    components: {
        GridRow,
        DataPreferenceSettings
    },
    props: {
        rowDatas: {
            type: Object as PropType<GridRowData[]>,
            required: true
        },
        isSubject: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            modalDataPreferenceSettings: {
                show: false,
                component: shallowRef(DataPreferenceSettings),
                defaultTabIdx: 0,
                hiddenTabIdx: [] as number[],
                fieldId: 0
            },
            modalImageViewer: {
                show: false,
                component: markRaw(ImageViewer),
                imagePath: 0
            },
            rowVisibilityByGroup: {} as Record<string, boolean>
        };
    },
    mounted() {
        (this as any).eventBus.on('launchDataPreferenceSettingsModal', (e: any) => {
            this.modalDataPreferenceSettings.show = true;
            this.modalDataPreferenceSettings.defaultTabIdx = e.defaultTabIdx || 0;
            this.modalDataPreferenceSettings.fieldId = e.fieldId || 0;
            // Hide Formatting Rules tab if Property Photo row is selected
            this.modalDataPreferenceSettings.hiddenTabIdx = e.fieldId === FieldIds.CompPhoto ? [1] : [];
        });
        (this as any).eventBus.on('launchFieldRulesModal', (e: any) => {
            this.modalDataPreferenceSettings.show = true;
            this.modalDataPreferenceSettings.defaultTabIdx = e.defaultTabIdx || 0;
            this.modalDataPreferenceSettings.fieldId = e.fieldId || 0;
        });
        (this as any).eventBus.on('launchImageViewerModal', (e: any) => {
            this.modalImageViewer.show = true;
            this.modalImageViewer.imagePath = e.imagePath;
        });
    },
    computed: {
        rows(): GridRowData[] {
            return this.rowDatas;
        },
        // These fields each end a multi-row repeatable block (e.g. Transfer History's Transfer
        // Present/Terms/Date/Amount/Data Source, or Listing Status/List Price). The old pure-CSS
        // `:has(~ ...)`/`:not(... ~ ...)` rule can only ever match the first such occurrence in
        // the whole table, since `~` has no notion of "immediately after this block" - unlike
        // ROOM_LEVEL, whose single-row repeats sit directly adjacent and so can use `+` instead.
        repeatableGroupDividerIndices(): Set<number> {
            const groupEndingFieldSuffixes = [
                'TRANSFER_HISTORY_SOURCE',
                'SLI_LIST_CUR_OR_FINAL_LP',
                'EXT_QUAL_BLANK',
                'EXT_COND_BLANK',
                'INT_QUAL_BLANK',
                'INT_COND_BLANK',
                'ADU_QUAL_BLANK',
                'ADU_COND_BLANK',
                'SUBCUROWNER',
            ];
            const indices = new Set<number>();
            groupEndingFieldSuffixes.forEach((suffix) => {
                const matchingIndices: number[] = [];
                this.rows.forEach((row, index) => {
                    if (String(row.fieldName || '').endsWith(suffix)) {
                        matchingIndices.push(index);
                    }
                });
                if (matchingIndices.length > 1) {
                    matchingIndices.slice(0, -1).forEach((i) => indices.add(i));
                }
            });
            return indices;
        },
        sectionHeaderLabelsByIndex(): Record<number, string> {
            const headerRows: Array<{ index: number; sectionName: string; sectionType: string; dwellingGroup: number }> = [];
            let totalDwellingSections = 0;
            let activeDwellingGroup = 0;
            const unitCountsByDwellingGroup: Record<number, number> = {};

            this.rows.forEach((row, index) => {
                if (!this.isSectionHeaderRow(index)) {
                    return;
                }

                const sectionName = row.sectionName ?? '';
                const sectionType = this.getSectionType(sectionName);

                if (sectionType === 'dwelling') {
                    totalDwellingSections++;
                    activeDwellingGroup = totalDwellingSections;
                    headerRows.push({ index, sectionName, sectionType, dwellingGroup: activeDwellingGroup });
                    return;
                }

                if (sectionType === 'unit') {
                    const dwellingGroup = activeDwellingGroup;
                    unitCountsByDwellingGroup[dwellingGroup] = (unitCountsByDwellingGroup[dwellingGroup] || 0) + 1;
                    headerRows.push({ index, sectionName, sectionType, dwellingGroup });
                    return;
                }

                headerRows.push({ index, sectionName, sectionType, dwellingGroup: activeDwellingGroup });
            });

            const labelsByIndex: Record<number, string> = {};
            let dwellingOrdinal = 0;
            const unitOrdinalsByDwellingGroup: Record<number, number> = {};

            headerRows.forEach((headerRow) => {
                if (headerRow.sectionType === 'dwelling') {
                    dwellingOrdinal++;
                    labelsByIndex[headerRow.index] = totalDwellingSections > 1 ? `Dwelling ${dwellingOrdinal}` : 'Dwelling';
                    return;
                }

                if (headerRow.sectionType === 'unit') {
                    const dwellingGroup = headerRow.dwellingGroup;
                    const nextUnitOrdinal = (unitOrdinalsByDwellingGroup[dwellingGroup] || 0) + 1;
                    unitOrdinalsByDwellingGroup[dwellingGroup] = nextUnitOrdinal;

                    const unitCountInCurrentDwelling = unitCountsByDwellingGroup[dwellingGroup] || 0;
                    labelsByIndex[headerRow.index] = unitCountInCurrentDwelling > 1 ? `Unit ${nextUnitOrdinal}` : 'Unit';
                    return;
                }

                labelsByIndex[headerRow.index] = headerRow.sectionName;
            });

            return labelsByIndex;
        }
    },
    methods: {
        toggleRowVisibility(payload: { groupKey: string; isVisible: boolean }) {
            this.rowVisibilityByGroup[payload.groupKey] = payload.isVisible;
            formFieldsStoreV36.saveRowVisibilityGrid({ [payload.groupKey]: payload.isVisible ? 'visible' : 'hidden' });

            // Only update the report's row visibility list if the field is already part of it —
            // never append a field that isn't already in the report list.
            if (Object.prototype.hasOwnProperty.call(formFieldsStoreV36.RowVisibilityReport ?? {}, payload.groupKey)) {
                formFieldsStoreV36.saveRowVisibilityReport({ [payload.groupKey]: payload.isVisible ? 'visible' : 'hidden' });
            }
        },
        isSectionHeaderRow(index: number): boolean {
            if (index < 0 || index >= this.rows.length) {
                return false;
            }

            const row = this.rows[index];
            const previousSectionName = index === 0 ? '' : this.rows[index - 1].sectionName;
            return !!row.sectionName && previousSectionName !== row.sectionName;
        },
        getSectionType(sectionName: string): string {
            const normalizedSectionName = (sectionName || '').toLowerCase();
            if (normalizedSectionName.includes('dwell')) {
                return 'dwelling';
            }

            if (normalizedSectionName.includes('unit')) {
                return 'unit';
            }

            return '';
        },
        getSectionHeaderLabel(sectionName: string, index: number): string {
            if (this.isOutbuildingSection(sectionName)) {
                const outbuildingType = this.getOutbuildingTypeValue(index);
                return outbuildingType ? `Outbuilding - ${outbuildingType}` : 'Outbuilding';
            }

            if (sectionName?.startsWith('Outbuilding#')) {
                return 'Outbuilding';
            }
            return this.sectionHeaderLabelsByIndex[index] ?? sectionName;
        },
        isOutbuildingSection(sectionName: string): boolean {
            return !!sectionName && sectionName.toLowerCase().startsWith('outbuilding');
        },
        getOutbuildingTypeValue(sectionStartIndex: number): string {
            const sectionName = this.rows[sectionStartIndex]?.sectionName;
            if (!sectionName) {
                return '';
            }

            for (let i = sectionStartIndex; i < this.rows.length; i++) {
                const row = this.rows[i];

                if (i > sectionStartIndex && row.sectionName !== sectionName) {
                    break;
                }

                const fieldName = String(row.fieldName || '');
                const isOutbuildingTypeField =
                    fieldName === 'OUTBUILDING_TYPE' ||
                    fieldName.endsWith('-OUTBUILDING_TYPE') ||
                    fieldName.startsWith('OUTBUILDING_TYPE-') ||
                    /^Outbuildings-\d+-OUTBUILDING_TYPE$/.test(fieldName) ||
                    /^\d+-OUTBUILDING_TYPE$/.test(fieldName);
                if (!isOutbuildingTypeField) {
                    continue;
                }

                const transferCellValue = row.cells?.[1]?.value;
                const transferCellSourceInitial = String(row.cells?.[1]?.sourceInitial || '').toLowerCase();
                if (
                    transferCellValue !== null &&
                    transferCellValue !== undefined &&
                    String(transferCellValue).trim() !== '' &&
                    transferCellSourceInitial !== 'ff'
                ) {
                    return String(transferCellValue);
                }

                if (Array.isArray(row.cells)) {
                    for (let c = 2; c < row.cells.length; c++) {
                        const sourceCell = row.cells[c];
                        if (!sourceCell || sourceCell.type !== GridColumnType.FeedData || sourceCell.isFakeData) {
                            continue;
                        }

                        const sourceCellValue = sourceCell.value;
                        if (sourceCellValue !== null && sourceCellValue !== undefined && String(sourceCellValue).trim() !== '') {
                            return String(sourceCellValue);
                        }
                    }
                }

                return '';
            }

            return '';
        },
        isUnitSection(sectionName: string): boolean {
            return !!sectionName && sectionName.toLowerCase().includes('unit');
        }
    }
});
